import * as React from 'react';

import { enumQuoteStatus } from '@rematter/frontend-lib-api-client';
import { MenuItem } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useSetAtom } from 'jotai';
import {
  LucideCopy,
  LucideDownload,
  LucidePrinter,
  LucideTrash2,
} from 'lucide-react';

import { $cloneQuoteId } from '../../../_state/quoteClone.state';
import { useTranslations } from '../../../i18n/useTranslations';
import { QuoteDetailRecord, QuoteRecord } from '../../../types/quote';

type LabelWithIconProps = {
  label: string;
  icon: React.ReactNode;
  className?: string;
};

const LabelWithIcon = ({ label, icon, className }: LabelWithIconProps) => (
  <div className={cn('@LabelWithIcon', 'flex items-center gap-2', className)}>
    {icon}
    {label}
  </div>
);

type UseQuoteDetailMenuItemsProps = {
  quote: QuoteRecord | QuoteDetailRecord | null;
  onVoid?: () => void;
};

export const useQuoteDetailMenuItems = ({
  quote,
  onVoid,
}: UseQuoteDetailMenuItemsProps): MenuItem[] => {
  const t = useTranslations();

  // Atoms
  const setCloneQuoteId = useSetAtom($cloneQuoteId);

  // Memoized values
  const canClone = React.useMemo(
    () => quote?.status !== enumQuoteStatus.Rejected,
    [quote?.status],
  );

  const canVoid =
    quote?.status !== enumQuoteStatus.Expired &&
    quote?.status !== enumQuoteStatus.Converted &&
    quote?.status !== enumQuoteStatus.Rejected &&
    quote?.status !== enumQuoteStatus.Voided;

  // Callbacks
  const handleClone = React.useCallback(() => {
    if (quote?.id) setCloneQuoteId(quote.id);
  }, [quote?.id, setCloneQuoteId]);

  const handlePrint = React.useCallback(() => {
    console.warn('printed');
  }, []);

  const handleDownload = React.useCallback(() => {
    console.warn('downloaded');
  }, []);

  return React.useMemo(
    () => [
      ...(canClone
        ? [
            {
              onAction: handleClone,
              label: (
                <LabelWithIcon
                  label={t.quotes.detail.actions.cloneQuote}
                  icon={<LucideCopy className="size-4" />}
                />
              ),
            },
          ]
        : []),
      {
        onAction: handlePrint,
        label: (
          <LabelWithIcon
            label={t.quotes.detail.actions.print}
            icon={<LucidePrinter className="size-4" />}
          />
        ),
      },
      {
        onAction: handleDownload,
        label: (
          <LabelWithIcon
            label={t.quotes.detail.actions.download}
            icon={<LucideDownload className="size-4" />}
          />
        ),
      },
      ...(canVoid && onVoid
        ? [
            {
              onAction: onVoid,
              label: (
                <LabelWithIcon
                  label={t.quotes.detail.actions.void}
                  icon={<LucideTrash2 className="size-4" />}
                  className="text-failure"
                />
              ),
            },
          ]
        : []),
    ],
    [canClone, canVoid, handleClone, handlePrint, handleDownload, onVoid, t],
  );
};
